/**
 * VisitorOfflineSync - Offline synchronization component for visitor management
 * Displays sync status, pending actions, and provides manual sync capabilities
 */

import React from 'react';
import {
  Wifi,
  WifiOff,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Clock,
  Users,
  UserCheck,
  UserX,
  Upload
} from 'lucide-react';
import useVisitorOffline from './hooks/useVisitorOffline';

const VisitorOfflineSync = () => {
  const {
    isOffline,
    pendingActions,
    syncStatus,
    lastSyncTime,
    offlineStats,
    syncPendingActions,
    clearPendingActions
  } = useVisitorOffline();

  const formatLastSyncTime = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getActionTypeLabel = (actionType) => {
    switch (actionType) {
      case 'REGISTER_VISITOR':
        return 'Register Visitor';
      case 'CHECK_IN_VISITOR':
        return 'Check In';
      case 'CHECK_OUT_VISITOR':
        return 'Check Out';
      case 'UPDATE_VISITOR':
        return 'Update Visitor';
      default:
        return actionType;
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'REGISTER_VISITOR':
        return <Users className="w-4 h-4" />;
      case 'CHECK_IN_VISITOR':
        return <UserCheck className="w-4 h-4" />;
      case 'CHECK_OUT_VISITOR':
        return <UserX className="w-4 h-4" />;
      case 'UPDATE_VISITOR':
        return <Upload className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getSyncStatusColor = () => {
    switch (syncStatus) {
      case 'syncing':
        return 'text-blue-600';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getSyncStatusIcon = () => {
    switch (syncStatus) {
      case 'syncing':
        return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'success':
        return <CheckCircle className="w-4 h-4" />;
      case 'error':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return isOffline ? <WifiOff className="w-4 h-4" /> : <Wifi className="w-4 h-4" />;
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`${getSyncStatusColor()}`}>
            {getSyncStatusIcon()}
          </div>
          <h3 className="text-sm font-medium text-gray-900">
            Visitor Sync Status
          </h3>
        </div>
        
        {/* Connection Status Badge */}
        <div className={`px-2 py-1 rounded-full text-xs font-medium ${
          isOffline 
            ? 'bg-red-100 text-red-800' 
            : 'bg-green-100 text-green-800'
        }`}>
          {isOffline ? 'Offline' : 'Online'}
        </div>
      </div>

      {/* Offline Statistics */}
      {isOffline && offlineStats && (
        <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              {offlineStats.visitors || 0}
            </div>
            <div className="text-xs text-gray-500">Offline Visitors</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              {offlineStats.photos || 0}
            </div>
            <div className="text-xs text-gray-500">Photos</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              {offlineStats.documents || 0}
            </div>
            <div className="text-xs text-gray-500">Documents</div>
          </div>
        </div>
      )}

      {/* Pending Actions */}
      {pendingActions.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Pending Actions ({pendingActions.length})
          </h4>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {pendingActions.slice(0, 5).map((action, index) => (
              <div key={index} className="flex items-center gap-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                <div className="text-yellow-600">
                  {getActionIcon(action.action_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {getActionTypeLabel(action.action_type)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {action.data?.visitor_name || action.data?.name || 'Unknown visitor'}
                  </div>
                </div>
                <div className="text-xs text-gray-400">
                  {new Date(action.created_at).toLocaleTimeString()}
                </div>
              </div>
            ))}
            {pendingActions.length > 5 && (
              <div className="text-xs text-gray-500 text-center py-1">
                +{pendingActions.length - 5} more actions
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sync Controls */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          Last sync: {formatLastSyncTime(lastSyncTime)}
        </div>
        
        <div className="flex gap-2">
          {pendingActions.length > 0 && (
            <button
              onClick={clearPendingActions}
              className="px-3 py-1 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
            >
              Clear
            </button>
          )}
          
          {!isOffline && pendingActions.length > 0 && (
            <button
              onClick={syncPendingActions}
              disabled={syncStatus === 'syncing'}
              className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-1"
            >
              {syncStatus === 'syncing' ? (
                <RefreshCw className="w-3 h-3 animate-spin" />
              ) : (
                <Upload className="w-3 h-3" />
              )}
              Sync Now
            </button>
          )}
        </div>
      </div>

      {/* Sync Status Message */}
      {syncStatus === 'success' && (
        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-800">
          ✓ All visitor data synchronized successfully
        </div>
      )}
      
      {syncStatus === 'error' && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          ⚠ Some actions failed to sync. Please try again.
        </div>
      )}
      
      {isOffline && (
        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
          📱 Working offline. Data will sync when connection is restored.
        </div>
      )}
    </div>
  );
};

export default VisitorOfflineSync;